import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { actualizarCompromiso } from "../../utils/compromisosPago";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// Edita la fecha estimada de pago (compromisos_pago.fecha_estimada) y el monto (compromisos_pago.monto)
// de un compromiso planificado SIN plan de cuotas, directamente desde el panel "Movimientos Futuros"
// (además de la ficha del proveedor). Escribe SOLO fecha_estimada + monto — no toca estado ni factura,
// así que no saca el compromiso de la proyección: solo lo reubica/reescala. Espejo de
// ModificarFechaProyeccionCapexModal. Los compromisos CON plan editan fecha/monto de cada cuota dentro
// del plan (ficha del proveedor). El monto es el valor CRUDO de la columna (neto si el compromiso nació
// con IVA desde la ficha; total si nació sin proveedor por el "+"); el grosseo/reexpresión lo hace la
// proyección, acá no.
//
// B.3 Commit 5 (2026-09-26, Fase 5 "Futuros: reagendar", camino #6): la FECHA migró a modo ESTRICTO —
// el modal ya no valida piso por su cuenta, el calendario compartido (SelectorFechaDocumento)
// deshabilita lo mismo que el writer rechaza (10.1). El MONTO sigue siendo un campo aparte, sin
// relación con el candado de fecha — su validación (obligatorio, >0) no cambia. El candado de
// "compromiso ya ejecutado sin plan" vive en la BASE (compromisos_pago_bloquea_fecha_estimada_trg,
// aplicado F2) — su rechazo llega visible al toast del catch (10.177).

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return "";
  return fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
};

const ModificarFechaProyeccionCompromisoModal = ({ show, onHide, entry, onSaved, pais }) => {
  const [fecha, setFecha] = useState(normalizarFecha(entry?.fecha_display));
  const [monto, setMonto] = useState(String(entry?.monto ?? ""));
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  const montoNum = Number(monto || 0);

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("La fecha es obligatoria");
      return;
    }
    if (montoNum <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    setGuardando(true);
    try {
      // Modo ESTRICTO fijo (camino #6, migrado): piso hoyDelPais(pais) + día hábil + candado de
      // "compromiso ya ejecutado sin plan" (BASE, compromisos_pago_bloquea_fecha_estimada_trg) —
      // cualquier rechazo llega acá como Error, visible en el catch de abajo (10.177).
      await actualizarCompromiso(
        entry.compromiso_id,
        { fecha_estimada: fecha, monto: montoNum },
        { modo: MODOS_VALIDACION.ESTRICTO, pais }
      );
      toast.success("Compromiso actualizado");
      // Patch puntual (Fase 2A): el padre actualiza `compromisosEntries` por compromiso_id, sin
      // refetch — ahora con fecha Y monto (los dos campos editables de este modal).
      onSaved?.({ fecha, monto: montoNum });
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Editar compromiso de pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Concepto:</strong> {entry.razon_social_receptor || "—"}
          </div>
        </div>

        <Form.Group style={{ marginBottom: 16 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>
            Monto {entry.moneda ? `(${entry.moneda})` : ""}
          </Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            disabled={guardando}
            style={montoNum <= 0 ? { borderColor: "#dc3545" } : undefined}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha estimada de pago</Form.Label>
          <SelectorFechaDocumento
            value={fecha}
            onChange={(nuevaFecha) => setFecha(nuevaFecha || "")}
            pais={pais}
            modo={MODOS_VALIDACION.ESTRICTO}
            disabled={guardando}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModificarFechaProyeccionCompromisoModal;
